import React, { useState, useEffect } from 'react';
import { X, Gamepad2, Plus, Copy, Trash2 } from 'lucide-react';
import * as imageService from '../services/imageService';

// Shared playground-config editor for a curated image.
//
// This used to live inline inside ClusterSharingModal, which meant playground
// configs could only be edited from a live cluster row (i.e. when a NATS worker
// was subscribed). It operates purely on the curated image's D1 record
// (`playground_config` + `openapi_schema`), so it works with no cluster running
// — the standalone PlaygroundConfigPage and the cluster modal both render it.
//
// Props:
//   image   — curated image object (needs id, name, playground_config, openapi_schema)
//   onSaved — called after a successful save (optional)

const PLAYGROUND_CATEGORIES = [
  { value: 'text-generation', label: 'Text Generation' },
  { value: 'text-to-image', label: 'Image Generation' },
  { value: 'image-text-to-text', label: 'Vision' },
  { value: 'text-to-video', label: 'Video Generation' },
  { value: 'automatic-speech-recognition', label: 'ASR' },
  { value: 'misc', label: 'Misc' },
];

const OUTPUT_TYPES = [
  { value: 'auto', label: 'Auto' },
  { value: 'skip', label: 'Skip' },
  { value: 'llm_chat', label: 'LLM Chat' },
  { value: 'text', label: 'Text' },
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
  { value: 'image_gallery', label: 'Image Gallery' },
  { value: 'video_gallery', label: 'Video Gallery' },
];

const CATEGORY_PRESETS = {
  'text-generation': [
    { key: 'prompt', label: 'Prompt', type: 'textarea' },
    { key: 'system_prompt', label: 'System Prompt', type: 'textarea' },
    { key: 'temperature', label: 'Temperature', type: 'slider', default: 0.7, min: 0, max: 2, step: 0.1 },
    { key: 'max_tokens', label: 'Max Tokens', type: 'number', default: 512, min: 1, max: 8192 },
    { key: 'top_p', label: 'Top P', type: 'slider', default: 0.9, min: 0, max: 1, step: 0.05 },
    { key: 'seed', label: 'Seed', type: 'number' },
  ],
  'text-to-image': [
    { key: 'prompt', label: 'Prompt', type: 'textarea' },
    { key: 'negative_prompt', label: 'Negative Prompt', type: 'textarea' },
    { key: 'width', label: 'Width', type: 'number', default: 1024, min: 64, max: 4096 },
    { key: 'height', label: 'Height', type: 'number', default: 1024, min: 64, max: 4096 },
    { key: 'steps', label: 'Steps', type: 'number', default: 20, min: 1, max: 150 },
    { key: 'guidance_scale', label: 'Guidance Scale', type: 'slider', default: 7.5, min: 0, max: 30, step: 0.5 },
    { key: 'seed', label: 'Seed', type: 'number' },
  ],
  'image-text-to-text': [
    { key: 'prompt', label: 'Prompt', type: 'textarea' },
    { key: 'image', label: 'Image', type: 'image' },
    { key: 'max_tokens', label: 'Max Tokens', type: 'number', default: 512, min: 1, max: 8192 },
    { key: 'temperature', label: 'Temperature', type: 'slider', default: 0.7, min: 0, max: 2, step: 0.1 },
  ],
  'text-to-video': [
    { key: 'prompt', label: 'Prompt', type: 'textarea' },
    { key: 'image_input', label: 'Image', type: 'image' },
    { key: 'video_input', label: 'Video', type: 'video' },
    { key: 'audio_input', label: 'Audio', type: 'audio' },
    { key: 'width', label: 'Width', type: 'number', default: 1024, min: 64, max: 4096 },
    { key: 'height', label: 'Height', type: 'number', default: 1024, min: 64, max: 4096 },
    { key: 'fps', label: 'FPS', type: 'number', default: 24, min: 1, max: 120 },
    { key: 'num_frames', label: 'Number of Frames', type: 'number', default: 24, min: 1, max: 300 },
    { key: 'steps', label: 'Steps', type: 'number', default: 20, min: 1, max: 150 },
    { key: 'seed', label: 'Seed', type: 'number' },
  ],
  'automatic-speech-recognition': [
    { key: 'audio', label: 'Audio', type: 'audio' },
    { key: 'language', label: 'Language', type: 'text' },
  ],
  'misc': [],
};

const normalizeConfig = (c) => ({
  ...c,
  name: c.name || 'Default',
  enabled: c.enabled ?? false,
  category: c.category || 'misc',
  mappings: c.mappings || {},
  defaults: c.defaults || {},
  // Starting values the user can still change, unlike `defaults`, which
  // hardcodes an unmapped API field and hides it. Keyed by preset key and by
  // template variable name respectively -- see convertConfigToInputs.
  notes: c.notes || '',
  inputDefaults: c.inputDefaults || {},
  jsonVarDefaults: c.jsonVarDefaults || {},
  jsonTemplate: c.jsonTemplate || null,
  output: c.output || { format: 'object', type: 'auto' },
  routing: c.routing || null,
});

// The 5 constraint tokens of a worker's NATS subject, after the MODEL token:
//   <MODEL>.<TYPE>.<CONTINENT>.<COUNTRY>.<PROVIDER>.<ACCELERATOR>
// (see orchestrator/converter/tcf_files/tcf_service.py). Each defaults to "*"
// (any); a concrete value pins that dimension so a workflow can require a GPU,
// region, or provider. The MODEL token stays bound to the selected model.
const ROUTING_TOKENS = [
  { key: 'type', label: 'Type', placeholder: 'e.g. DATACENTER' },
  { key: 'continent', label: 'Continent', placeholder: 'e.g. EU / NA / AS' },
  { key: 'country', label: 'Country', placeholder: 'e.g. FR / US' },
  { key: 'provider', label: 'Provider', placeholder: 'e.g. SCW / AWS / VAST' },
  { key: 'accelerator', label: 'Accelerator (GPU)', placeholder: 'e.g. RTX4090 / A100' },
];

// Sanitize a routing token to the form workers emit (upper-case, NATS-safe),
// preserving the `*`/`>` wildcards. Empty → `*` (any).
const sanitizeRoutingToken = (v) => {
  const s = (v ?? '').toString().trim();
  if (!s || s === '*') return '*';
  return s.toUpperCase().replace(/[^A-Z0-9_*>]/g, '_');
};

const routingIsConstrained = (routing) =>
  !!routing && ROUTING_TOKENS.some(t => sanitizeRoutingToken(routing[t.key]) !== '*');

const PlaygroundConfigEditor = ({ image, onSaved }) => {
  const rawPgConfig = image?.playground_config;

  const initConfigs = () => {
    if (Array.isArray(rawPgConfig) && rawPgConfig.length > 0) return rawPgConfig.map(normalizeConfig);
    if (rawPgConfig && typeof rawPgConfig === 'object' && rawPgConfig.enabled) {
      return [normalizeConfig({ name: rawPgConfig.category || 'Default', ...rawPgConfig })];
    }
    return [];
  };

  const [pgConfigs, setPgConfigs] = useState(initConfigs);
  const [activePgIdx, setActivePgIdx] = useState(0);
  const [savingPg, setSavingPg] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // Reset local state when the target image changes (page switches between images)
  useEffect(() => {
    setPgConfigs(initConfigs());
    setActivePgIdx(0);
    setSaved(false);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image?.id]);

  const pgConfig = pgConfigs[activePgIdx] || null;
  const setPgConfig = (updater) => {
    setPgConfigs(prev => prev.map((c, i) => i === activePgIdx ? (typeof updater === 'function' ? updater(c) : updater) : c));
  };

  const addPgConfig = () => {
    const newConfig = {
      name: `Config ${pgConfigs.length + 1}`,
      enabled: true,
      category: 'misc',
      mappings: {},
      defaults: {},
      notes: '',
      inputDefaults: {},
      jsonVarDefaults: {},
      jsonTemplate: null,
      output: { format: 'object', type: 'auto' },
    };
    setPgConfigs(prev => [...prev, newConfig]);
    setActivePgIdx(pgConfigs.length);
  };

  const removePgConfig = (idx) => {
    setPgConfigs(prev => prev.filter((_, i) => i !== idx));
    setActivePgIdx(prev => Math.min(prev, pgConfigs.length - 2));
  };

  const duplicatePgConfig = (idx) => {
    const copy = { ...JSON.parse(JSON.stringify(pgConfigs[idx])), name: pgConfigs[idx].name + ' (copy)' };
    setPgConfigs(prev => [...prev, copy]);
    setActivePgIdx(pgConfigs.length);
  };

  // Preset management (DB-backed)
  const [savedPresets, setSavedPresets] = useState([]);

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchPresets = async () => {
    try {
      const data = await imageService.listPlaygroundPresets();
      if (data.success) setSavedPresets(data.presets || []);
    } catch { /* ignore */ }
  };

  const saveAsPreset = async (config) => {
    const name = window.prompt('Preset name:', config.name || 'My Preset');
    if (!name) return;
    const { name: _ignored, ...rest } = config;
    try {
      await imageService.savePlaygroundPreset(name, rest);
      await fetchPresets();
    } catch (err) { setError(err.message); }
  };

  const loadPreset = (presetId) => {
    const preset = savedPresets.find(p => p.id === presetId);
    if (!preset) return;
    const config = typeof preset.config === 'string' ? JSON.parse(preset.config) : preset.config;
    setPgConfig(prev => normalizeConfig({ ...prev, ...config, name: prev.name || preset.name }));
  };

  const deletePreset = async (presetId) => {
    if (!window.confirm('Delete this preset?')) return;
    try {
      await imageService.deletePlaygroundPreset(presetId);
      setSavedPresets(prev => prev.filter(p => p.id !== presetId));
    } catch (err) { setError(err.message); }
  };

  const savePgConfigs = async () => {
    if (!image) return;
    setSavingPg(true); setError(null); setSaved(false);
    try {
      const playgroundConfig = pgConfigs.length > 0 ? pgConfigs : null;
      await imageService.setImagePlaygroundConfig(image.id, playgroundConfig);
      setSaved(true);
      if (onSaved) onSaved();
    } catch (err) { setError(err.message); }
    finally { setSavingPg(false); }
  };

  const setMapping = (presetKey, apiField) => {
    setPgConfig(prev => ({ ...prev, mappings: { ...prev.mappings, [presetKey]: apiField } }));
  };

  const setCategory = (cat) => {
    setPgConfig(prev => ({ ...prev, category: cat }));
  };

  const setRoutingToken = (key, value) => {
    setPgConfig(prev => {
      const routing = { ...(prev.routing || {}), [key]: value };
      // Collapse to null when every token is "any" so the config stays clean.
      return { ...prev, routing: routingIsConstrained(routing) ? routing : null };
    });
  };

  const setJsonTemplate = (template) => {
    const vars = [...new Set((template.match(/\$\(([^)]+)\)/g) || []).map(m => m.slice(2, -1)))];
    const existingMap = pgConfig.jsonTemplate?.variableMap || {};
    const variableMap = {};
    vars.forEach(v => { variableMap[v] = existingMap[v] || ''; });
    setPgConfig(prev => ({ ...prev, jsonTemplate: { ...prev.jsonTemplate, template, variableMap } }));
  };

  const setJsonVariableMapping = (varName, presetKey) => {
    setPgConfig(prev => ({
      ...prev,
      jsonTemplate: { ...prev.jsonTemplate, variableMap: { ...prev.jsonTemplate.variableMap, [varName]: presetKey } },
    }));
  };

  const setJsonTargetField = (field) => {
    setPgConfig(prev => ({ ...prev, jsonTemplate: { ...prev.jsonTemplate, targetField: field } }));
  };

  const presets = pgConfig ? (CATEGORY_PRESETS[pgConfig.category] || []) : [];

  // Extract OpenAPI input field names for dropdowns
  const openapiFields = Object.keys(image?.openapi_schema?.components?.schemas?.Input?.properties || {});
  const openapiProps = image?.openapi_schema?.components?.schemas?.Input?.properties || {};
  const openapiRequired = new Set(image?.openapi_schema?.components?.schemas?.Input?.required || []);

  const jsonConsumedPresets = new Set(
    Object.values(pgConfig?.jsonTemplate?.variableMap || {}).filter(Boolean)
  );
  const directPresets = presets.filter(p => !jsonConsumedPresets.has(p.key));

  const usedApiFields = new Set([
    ...Object.values(pgConfig?.mappings || {}).filter(Boolean),
    pgConfig?.jsonTemplate?.targetField,
  ].filter(Boolean));
  const unmappedApiFields = openapiFields.filter(f => !usedApiFields.has(f));

  const setDefault = (fieldName, value) => {
    setPgConfig(prev => ({ ...prev, defaults: { ...prev.defaults, [fieldName]: value } }));
  };

  // An empty box means "no default", so the key is dropped rather than stored
  // as '' -- otherwise convertConfigToInputs would see a defined-but-empty
  // value and shadow the category catalogue's default with nothing.
  const setKeyedDefault = (bag, key, value) => {
    setPgConfig(prev => {
      const next = { ...(prev[bag] || {}) };
      if (value === '' || value === undefined) delete next[key];
      else next[key] = value;
      return { ...prev, [bag]: next };
    });
  };
  const setInputDefault = (presetKey, value) => setKeyedDefault('inputDefaults', presetKey, value);
  const setJsonVarDefault = (varName, value) => setKeyedDefault('jsonVarDefaults', varName, value);

  const typeLabel = (t) => {
    const map = { textarea: 'Text', text: 'Text', number: 'Num', slider: 'Slider', image: 'File', video: 'File', audio: 'File', toggle: 'Bool' };
    return map[t] || t;
  };

  const renderFieldInput = (fieldName, field, value, onChange) => {
    const fieldType = field.type || (field.allOf ? 'object' : 'string');

    if (field.enum && Array.isArray(field.enum)) {
      return (
        <select value={value ?? (field.default !== undefined ? String(field.default) : '')}
          onChange={(e) => onChange(e.target.value)}
          className={`flex-1 border rounded px-2 py-1 text-xs font-mono focus:ring-1 focus:ring-purple-500 focus:border-transparent ${value ? 'border-purple-300 bg-purple-50' : 'border-gray-300'}`}>
          <option value="">— select —</option>
          {field.enum.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      );
    }

    if (fieldType === 'boolean') {
      const boolVal = value === 'true' || value === true;
      return (
        <button type="button" onClick={() => onChange(boolVal ? 'false' : 'true')}
          className={`w-10 h-5 rounded-full relative transition-all shrink-0 ${boolVal ? 'bg-purple-600' : 'bg-gray-300'}`}>
          <div className={`absolute top-[2px] left-[2px] w-[16px] h-[16px] rounded-full bg-white shadow transition-transform ${boolVal ? 'translate-x-5' : ''}`} />
        </button>
      );
    }

    if (fieldType === 'integer') {
      return (
        <input type="number" step="1"
          min={field.minimum ?? field.ge} max={field.maximum ?? field.le}
          value={value ?? (field.default !== undefined ? field.default : '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.default !== undefined ? String(field.default) : 'empty'}
          className={`flex-1 border rounded px-2 py-1 text-xs font-mono focus:ring-1 focus:ring-purple-500 focus:border-transparent ${value ? 'border-purple-300 bg-purple-50' : 'border-gray-300'}`}
        />
      );
    }

    if (fieldType === 'number') {
      return (
        <input type="number" step="any"
          min={field.minimum ?? field.ge} max={field.maximum ?? field.le}
          value={value ?? (field.default !== undefined ? field.default : '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.default !== undefined ? String(field.default) : 'empty'}
          className={`flex-1 border rounded px-2 py-1 text-xs font-mono focus:ring-1 focus:ring-purple-500 focus:border-transparent ${value ? 'border-purple-300 bg-purple-50' : 'border-gray-300'}`}
        />
      );
    }

    return (
      <input type="text"
        value={value ?? (field.default !== undefined ? String(field.default) : '')}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.default !== undefined ? String(field.default) : 'empty'}
        className={`flex-1 border rounded px-2 py-1 text-xs font-mono focus:ring-1 focus:ring-purple-500 focus:border-transparent ${value ? 'border-purple-300 bg-purple-50' : 'border-gray-300'}`}
      />
    );
  };

  const hasSchema = openapiFields.length > 0;

  return (
    <div className="space-y-3">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gamepad2 size={16} className={pgConfigs.length > 0 ? 'text-purple-600' : 'text-gray-400'} />
          <label className="text-sm font-medium text-gray-700">Playground</label>
        </div>
        <button type="button" onClick={addPgConfig}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-600 hover:text-purple-800 border border-purple-300 rounded hover:bg-purple-50 transition-colors">
          <Plus size={12} /> Add Config
        </button>
      </div>

      {!hasSchema && (
        <p className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          This image has no converted OpenAPI schema yet. You can still define
          configs, but API field dropdowns fall back to free-text entry. Convert
          the image (TCF) to unlock schema-aware mapping.
        </p>
      )}

      {pgConfigs.length === 0 && (
        <p className="text-sm text-gray-400 italic py-3 text-center">
          No playground configs yet. Click "Add Config" to expose this image in the Playground.
        </p>
      )}

      {pgConfigs.length > 0 && (
        <>
          {/* Config tabs */}
          <div className="flex gap-1 flex-wrap">
            {pgConfigs.map((cfg, idx) => (
              <button key={idx} type="button" onClick={() => setActivePgIdx(idx)}
                className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                  idx === activePgIdx ? 'bg-purple-100 text-purple-800 border-purple-300' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                }`}>
                {cfg.name || `Config ${idx + 1}`}
              </button>
            ))}
          </div>

          {pgConfig && (
            <>
              {/* Playground name + actions */}
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-600">Playground Name</label>
                <p className="text-[10px] text-gray-400">Display name shown in the playground model list</p>
              </div>
              <div className="flex items-center gap-2">
                <input type="text" value={pgConfig.name || ''}
                  onChange={(e) => setPgConfig(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={image?.name || 'Playground name'}
                  className="flex-1 border border-gray-300 rounded px-2.5 py-1.5 text-sm focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                />
                <button type="button" onClick={() => saveAsPreset(pgConfig)}
                  className="text-xs text-purple-600 hover:text-purple-800 border border-purple-300 rounded px-2 py-1 hover:bg-purple-50 whitespace-nowrap" title="Save as reusable preset">
                  Save Preset
                </button>
                <button type="button" onClick={() => duplicatePgConfig(activePgIdx)}
                  className="text-gray-400 hover:text-purple-600 p-1" title="Duplicate">
                  <Copy size={14} />
                </button>
                <button type="button" onClick={() => removePgConfig(activePgIdx)}
                  className="text-gray-400 hover:text-red-500 p-1" title="Delete config">
                  <Trash2 size={14} />
                </button>
              </div>

              {/* Load from preset */}
              {savedPresets.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-gray-500 whitespace-nowrap">Load preset:</span>
                  <select onChange={(e) => { if (e.target.value) loadPreset(Number(e.target.value)); e.target.value = ''; }}
                    className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-purple-500 focus:border-transparent" defaultValue="">
                    <option value="">— select preset —</option>
                    {savedPresets.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <select onChange={(e) => { if (e.target.value) deletePreset(Number(e.target.value)); e.target.value = ''; }}
                    className="border border-gray-300 rounded px-1.5 py-1 text-[10px] text-red-500" defaultValue="" title="Delete a preset">
                    <option value="">Del</option>
                    {savedPresets.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Category */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                <select value={pgConfig.category} onChange={(e) => setCategory(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                  {PLAYGROUND_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>

              {/* NATS routing — pin region / GPU / provider, or leave "any" */}
              <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                <label className="block text-xs font-medium text-gray-600">Worker routing — region / GPU (optional)</label>
                <p className="text-[11px] text-gray-400">
                  Constrain which workers serve this config. Each dimension is a
                  routing token; leave blank for <code className="bg-gray-100 px-1 rounded">*</code> (any).
                  A request is dispatched only to a live worker that matches.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {ROUTING_TOKENS.map(tok => (
                    <div key={tok.key}>
                      <label className="block text-[10px] font-medium text-gray-500 mb-0.5">{tok.label}</label>
                      <input type="text"
                        value={pgConfig.routing?.[tok.key] ?? ''}
                        onChange={(e) => setRoutingToken(tok.key, e.target.value)}
                        placeholder={`${tok.placeholder} — blank = any`}
                        className={`w-full border rounded px-2 py-1 text-xs font-mono focus:ring-1 focus:ring-purple-500 focus:border-transparent ${
                          sanitizeRoutingToken(pgConfig.routing?.[tok.key]) !== '*' ? 'border-purple-300 bg-purple-50' : 'border-gray-300'
                        }`}
                      />
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-gray-500 font-mono bg-gray-50 border border-gray-200 rounded px-2 py-1 truncate">
                  {'<model>.' + ROUTING_TOKENS.map(t => sanitizeRoutingToken(pgConfig.routing?.[t.key])).join('.')}
                  {!routingIsConstrained(pgConfig.routing) && <span className="text-gray-400 not-italic"> — no constraint (any worker)</span>}
                </div>
              </div>

              {/* Preset input mappings */}
              {directPresets.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Input Mapping</label>
                  <p className="text-[11px] text-gray-400 mb-2">Map each standard input to the model's API field. Leave empty to hide from playground. The box on the right is the default value the playground starts with &mdash; the user can still change it.</p>
                  <div className="space-y-1.5">
                    {directPresets.map(preset => (
                      <div key={preset.key} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                        <div className="w-36 flex items-center gap-1.5">
                          <span className="text-xs font-medium text-gray-700">{preset.label}</span>
                          <span className="text-[10px] text-gray-400 font-mono">({typeLabel(preset.type)})</span>
                        </div>
                        <span className="text-gray-400 text-xs">→</span>
                        {openapiFields.length > 0 ? (
                          <select
                            value={pgConfig.mappings[preset.key] || ''}
                            onChange={(e) => setMapping(preset.key, e.target.value)}
                            className={`flex-1 border rounded px-2 py-1 text-xs font-mono focus:ring-1 focus:ring-purple-500 focus:border-transparent ${
                              pgConfig.mappings[preset.key] ? 'border-purple-300 bg-purple-50' : 'border-gray-300'
                            }`}>
                            <option value="">— select API field —</option>
                            {openapiFields.map(f => (
                              <option key={f} value={f}>{f}</option>
                            ))}
                          </select>
                        ) : (
                          <input type="text"
                            value={pgConfig.mappings[preset.key] || ''}
                            onChange={(e) => setMapping(preset.key, e.target.value)}
                            placeholder="api_field_name"
                            className={`flex-1 border rounded px-2 py-1 text-xs font-mono focus:ring-1 focus:ring-purple-500 focus:border-transparent ${
                              pgConfig.mappings[preset.key] ? 'border-purple-300 bg-purple-50' : 'border-gray-300'
                            }`}
                          />
                        )}
                        {pgConfig.mappings[preset.key] && (
                          <input
                            type={['number', 'slider'].includes(preset.type) ? 'number' : 'text'}
                            value={pgConfig.inputDefaults?.[preset.key] ?? ''}
                            onChange={(e) => setInputDefault(preset.key, e.target.value)}
                            min={preset.min} max={preset.max} step={preset.step}
                            placeholder={preset.default !== undefined ? String(preset.default) : 'default'}
                            title={`Starting value for ${preset.label} in the playground. The user can still change it.`}
                            className="w-24 border border-gray-300 rounded px-2 py-1 text-xs font-mono focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                          />
                        )}
                        {pgConfig.mappings[preset.key] && (
                          <button type="button" onClick={() => setMapping(preset.key, '')}
                            className="text-gray-400 hover:text-red-500 p-0.5" title="Clear mapping">
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* JSON Template (for ComfyUI workflows etc.) */}
              <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                <label className="block text-xs font-medium text-gray-600">JSON Workflow Template (optional)</label>
                <p className="text-[11px] text-gray-400">
                  Paste a JSON workflow (e.g. ComfyUI). Use <code className="bg-gray-100 px-1 rounded">$(VARIABLE)</code> for values to replace at runtime.
                </p>
                <textarea
                  value={pgConfig.jsonTemplate?.template || ''}
                  onChange={(e) => setJsonTemplate(e.target.value)}
                  placeholder='{"6": {"inputs": {"text": $(PROMPT), ...}}}'
                  rows={5}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-[11px] font-mono focus:ring-1 focus:ring-purple-500 focus:border-transparent resize-y"
                />
                {pgConfig.jsonTemplate?.variableMap && Object.keys(pgConfig.jsonTemplate.variableMap).length > 0 && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded p-2 space-y-1.5">
                    <span className="text-[11px] font-medium text-indigo-700">Detected variables:</span>
                    <p className="text-[10.5px] text-indigo-500/80">Pick the input each variable takes its value from, and optionally a default. Without one the variable starts empty and substitutes as <code className="bg-indigo-100 px-1 rounded">""</code>.</p>
                    {Object.entries(pgConfig.jsonTemplate.variableMap).map(([varName, mappedKey]) => (
                      <div key={varName} className="flex items-center gap-2">
                        <code className="text-[11px] text-indigo-600 bg-indigo-100 px-1.5 py-0.5 rounded font-mono min-w-[80px]">${'$'}({varName})</code>
                        <span className="text-[11px] text-gray-500">→</span>
                        <select value={mappedKey}
                          onChange={(e) => setJsonVariableMapping(varName, e.target.value)}
                          className="flex-1 border border-gray-300 rounded px-1.5 py-0.5 text-[11px] focus:ring-1 focus:ring-purple-500 focus:border-transparent">
                          <option value="">— select input —</option>
                          {presets.map(p => (
                            <option key={p.key} value={p.key}>{p.label}</option>
                          ))}
                        </select>
                        {(() => {
                          const preset = presets.find(p => p.key === mappedKey);
                          return (
                            <input
                              type={['number', 'slider'].includes(preset?.type) ? 'number' : 'text'}
                              value={pgConfig.jsonVarDefaults?.[varName] ?? ''}
                              onChange={(e) => setJsonVarDefault(varName, e.target.value)}
                              min={preset?.min} max={preset?.max} step={preset?.step}
                              placeholder={preset?.default !== undefined ? String(preset.default) : 'default'}
                              title={`Starting value substituted for $(${varName}). The user can still change it.`}
                              className="w-24 border border-gray-300 rounded px-1.5 py-0.5 text-[11px] font-mono focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                            />
                          );
                        })()}
                      </div>
                    ))}
                  </div>
                )}
                {pgConfig.jsonTemplate?.template && (
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded p-2">
                    <span className="text-[11px] font-medium text-amber-700 whitespace-nowrap">Send JSON to API field:</span>
                    {openapiFields.length > 0 ? (
                      <select value={pgConfig.jsonTemplate?.targetField || ''}
                        onChange={(e) => setJsonTargetField(e.target.value)}
                        className="flex-1 border border-gray-300 rounded px-1.5 py-0.5 text-[11px] font-mono focus:ring-1 focus:ring-amber-500 focus:border-transparent">
                        <option value="">— select API field —</option>
                        {openapiFields.map(f => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                    ) : (
                      <input type="text"
                        value={pgConfig.jsonTemplate?.targetField || ''}
                        onChange={(e) => setJsonTargetField(e.target.value)}
                        placeholder="api_field_name"
                        className="flex-1 border border-gray-300 rounded px-1.5 py-0.5 text-[11px] font-mono focus:ring-1 focus:ring-amber-500 focus:border-transparent"
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Unmapped OpenAPI fields — smart input widgets */}
              {unmappedApiFields.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Other API Fields</label>
                  <p className="text-[11px] text-gray-400 mb-2">Fields not mapped to a preset input. Set a default value or leave empty for non-required fields.</p>
                  <div className="space-y-1.5">
                    {unmappedApiFields.map(fieldName => {
                      const field = openapiProps[fieldName] || {};
                      const isRequired = openapiRequired.has(fieldName);
                      const isNullable = field.nullable === true || field['x-nullable'] === true;
                      const isSkipped = pgConfig.defaults[fieldName] === null;
                      const fieldType = field.type || (field.allOf ? 'object' : field.format || 'any');
                      const hasRange = field.minimum !== undefined || field.maximum !== undefined || field.ge !== undefined || field.le !== undefined;
                      return (
                        <div key={fieldName} className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-200 space-y-1">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <code className="text-xs font-medium text-gray-700 truncate">{fieldName}</code>
                              {isRequired && (
                                <span className="text-[9px] bg-red-100 text-red-700 px-1 py-0.5 rounded shrink-0">req</span>
                              )}
                              {isNullable && (
                                <span className="text-[9px] bg-yellow-100 text-yellow-700 px-1 py-0.5 rounded shrink-0">null</span>
                              )}
                            </div>
                            <span className="text-[10px] text-gray-400 font-mono shrink-0">{fieldType}</span>
                            {hasRange && (
                              <span className="text-[10px] text-gray-400 font-mono shrink-0">
                                [{field.minimum ?? field.ge ?? '...'}, {field.maximum ?? field.le ?? '...'}]
                              </span>
                            )}
                            <div className="flex-1 flex items-center gap-1.5">
                              {isNullable && (
                                <label className="flex items-center gap-1 shrink-0 cursor-pointer" title="Leave empty (null)">
                                  <input type="checkbox" checked={isSkipped}
                                    onChange={(e) => setDefault(fieldName, e.target.checked ? null : '')}
                                    className="w-3 h-3 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                  />
                                  <span className="text-[10px] text-gray-500">skip</span>
                                </label>
                              )}
                              {!isSkipped && renderFieldInput(fieldName, field, pgConfig.defaults[fieldName], (v) => setDefault(fieldName, v))}
                              {isSkipped && (
                                <span className="text-[10px] text-gray-400 italic">will not be sent</span>
                              )}
                            </div>
                          </div>
                          {field.description && (
                            <p className="text-[10px] text-gray-500 leading-tight">{field.description}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Operator note shown in the playground, under the settings */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Playground Note</label>
                <p className="text-[11px] text-gray-400 mb-2">
                  Shown to the user in the playground, under the parameters. Use it for the things
                  the form cannot express &mdash; compatible resolutions, parameter limits, what a
                  field actually does on this model.
                </p>
                <textarea
                  value={pgConfig.notes || ''}
                  onChange={(e) => setPgConfig(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  placeholder={'e.g. Width and height must be multiples of 32. 1280x704 is the tested resolution.'}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-[11px] focus:ring-1 focus:ring-purple-500 focus:border-transparent resize-y"
                />
              </div>

              {/* Output Configuration */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Output</label>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] text-gray-500">Response format:</span>
                    <div className="flex gap-1">
                      {['object', 'array'].map(fmt => (
                        <button key={fmt} type="button"
                          onClick={() => setPgConfig(prev => ({
                            ...prev,
                            output: fmt === 'object'
                              ? { format: 'object', type: prev.output?.type || 'auto' }
                              : { format: 'array', items: prev.output?.items || [{ type: 'auto' }] },
                          }))}
                          className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                            pgConfig.output?.format === fmt
                              ? 'bg-purple-100 text-purple-800 border-purple-300'
                              : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                          }`}>
                          {fmt === 'object' ? 'Object' : 'Array'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {pgConfig.output?.format === 'object' && (
                    <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                      <span className="text-xs text-gray-600">Output type:</span>
                      <select value={pgConfig.output?.type || 'auto'}
                        onChange={(e) => setPgConfig(prev => ({ ...prev, output: { ...prev.output, type: e.target.value } }))}
                        className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-purple-500 focus:border-transparent">
                        {OUTPUT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                  )}

                  {pgConfig.output?.format === 'array' && (
                    <div className="space-y-1.5">
                      {(pgConfig.output?.items || []).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                          <span className="text-xs text-gray-500 font-mono w-12 shrink-0">[{idx}]</span>
                          <select value={item.type || 'auto'}
                            onChange={(e) => {
                              const items = [...(pgConfig.output?.items || [])];
                              items[idx] = { ...items[idx], type: e.target.value };
                              setPgConfig(prev => ({ ...prev, output: { ...prev.output, items } }));
                            }}
                            className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-purple-500 focus:border-transparent">
                            {OUTPUT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                          <button type="button"
                            onClick={() => {
                              const items = (pgConfig.output?.items || []).filter((_, i) => i !== idx);
                              if (items.length === 0) items.push({ type: 'auto' });
                              setPgConfig(prev => ({ ...prev, output: { ...prev.output, items } }));
                            }}
                            className="text-gray-400 hover:text-red-500 p-0.5" title="Remove">
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      <button type="button"
                        onClick={() => {
                          const items = [...(pgConfig.output?.items || []), { type: 'auto' }];
                          setPgConfig(prev => ({ ...prev, output: { ...prev.output, items } }));
                        }}
                        className="text-xs text-purple-600 hover:text-purple-800 font-medium">
                        + Add index
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end items-center gap-3">
            {saved && <span className="text-xs text-green-600 font-medium">Saved ✓</span>}
            <button type="button" onClick={savePgConfigs} disabled={savingPg}
              className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-xs font-medium disabled:opacity-50">
              {savingPg ? 'Saving...' : 'Save Playground Config'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PlaygroundConfigEditor;
